'use client';
/* eslint-disable @next/next/no-img-element */
import React from 'react';
import { Image, cn } from '@nextui-org/react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { tv } from '@nextui-org/react';
import View from '../view';

const heading = tv({
  base: 'text-2xl font-bold',
  variants: {
    color: {
      success: 'text-[var(--app-color-green)]',
      default: 'text-default-900 ',
    },
  },
});

type HeadingProps = {
  as?: 'h1' | 'h2' | 'h3' | 'h4';
  color?: 'success' | 'default';
} & React.ComponentPropsWithoutRef<'h1'>;

const Heading: React.FC<HeadingProps> = ({
  as: Component = 'h1',
  color = 'default',
  ...props
}) => {
  return (
    <div className="flex flex-col">
      <Component
        {...props}
        className={`${heading({
          color,
        })} ${props.className}`}
      />
    </div>
  );
};
const Description = (props: React.ComponentPropsWithoutRef<'p'>) => {
  return (
    <div className="flex flex-col ">
      <p
        {...props}
        className={cn(
          'text-default-600 text-base leading-relaxed font-medium',
          props.className
        )}
      />
    </div>
  );
};
const Preview = (props: React.ComponentProps<typeof Image>) => {
  return (
    <div className="flex flex-col flex-1">
      <Image
        loading="lazy"
        alt={props.alt}
        classNames={{
          wrapper: 'h-full',
          img: 'h-full max-h-full',
          zoomedWrapper: 'h-full',
        }}
        {...props}
        className={cn('max-w-full h-full', props.className)}
      />
    </div>
  );
};

const Blockquote = (props: React.ComponentPropsWithoutRef<'blockquote'>) => {
  return (
    <div
      className={cn(
        'shadow-md py-6 px-4 border-l-4 border-[var(--app-color-green)] rounded-md bg-default-50'
      )}
    >
      <blockquote {...props} />
    </div>
  );
};

type PProps = {
  next?: React.ComponentPropsWithoutRef<'button'>;
  previous?: React.ComponentPropsWithoutRef<'button'>;
};

const Pagination = ({ next, previous }: PProps) => {
  return (
    <div className="my-10 flex justify-between border-2 rounded-lg border-dotted border-default-200 border-opacity-40 p-4">
      <div className="button-transition">
        {previous && (
          <button
            {...previous}
            className={cn(
              'bg-basic text-white py-3 px-8 rounded-md flex items-center gap-2',
              previous.className
            )}
          >
            <ArrowLeftIcon className="w-4 left" />
            <span>{previous.content || previous.children}</span>
          </button>
        )}
      </div>
      <div className="button-transition">
        {next && (
          <button
            {...next}
            className={cn(
              'bg-basic text-white py-3 px-8 rounded-md flex items-center gap-2',
              next.className
            )}
          >
            <span>{next.content || next.children}</span>
            <ArrowRightIcon className="w-4 right" />
          </button>
        )}
      </div>
    </div>
  );
};

type AsideProps = {
  heading?: string;
} & React.ComponentPropsWithoutRef<'aside'>;
const Aside = ({ heading, children, ...rest }: AsideProps) => {
  return (
    <View className="sticky top-24 border border-default-100 rounded-lg h-fit bg-white">
      <h3 className="relative px-3 py-4 bg-basic text-white text-2xl font-semibold rounded-t-lg">
        {heading}
        <img
          src="/assest/pot-leaf-sidebar.png"
          alt="leaf"
          width={70}
          height={50}
          className="absolute right-0 top-0"
        />
      </h3>
      {children}
    </View>
  );
};

const Detailed = {
  Heading,
  Description,
  Preview,
  Blockquote,
  Pagination,
  Aside,
};

export default Detailed;
